/**
 * Non-flow API types shared between the apiClient interface and its
 * implementations. Kept separate from flow.ts to avoid circular imports.
 */

import type { CaptureMode, SourceBinding } from "./flow";

// ─── Fixtures (simulator test data) ───────────────────────────────────────────

export interface FlowFixture {
  id: string;
  flow_id: string;
  name: string;
  description: string | null;
  context: Record<string, unknown>;
  source: SourceBinding | null;
  expected_terminal_node_id: string | null;
  expected_capture_mode: CaptureMode | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFlowFixtureInput {
  name: string;
  description?: string;
  context: Record<string, unknown>;
  source?: SourceBinding;
  expected_terminal_node_id?: string;
  expected_capture_mode?: CaptureMode;
}

// ─── Templates ────────────────────────────────────────────────────────────────

export interface FlowTemplateSummary {
  id: string;
  name: string;
  description: string;
  exercises: string[];
  preview_image_url?: string;
}

export interface FlowTemplateCatalog {
  templates: FlowTemplateSummary[];
}

export interface ForkFromTemplateInput {
  template_id: string;
  name: string;
}

// ─── Media (banner uploads) ───────────────────────────────────────────────────

export interface MediaBannerMetadata {
  image_id: string;
  cdn_url: string;
  format: "jpeg" | "png" | "webp";
  bytes: number;
  width: number;
  height: number;
}

// ─── Fraud providers ──────────────────────────────────────────────────────────

export type FraudProviderStatus =
  | {
      provider: "cost_plus";
      configured: true;
      builtin: true;
    }
  | {
      provider: "shopify";
      configured: boolean;
      requires_shopify_install: true;
    }
  | {
      provider: "sift" | "signifyd";
      configured: boolean;
      last_used_at: string | null;
      thresholds: { low_max: number; medium_max: number } | null;
    };

export interface FraudProviderList {
  providers: FraudProviderStatus[];
}

export interface PutFraudProviderInput {
  api_key: string;
  thresholds?: { low_max: number; medium_max: number };
}

// ─── A/B analytics ────────────────────────────────────────────────────────────

export interface ABVariantRollup {
  id: string;
  sessions: number;
  conversions: number;
  conversion_rate: number;
  revenue_cents: number;
  currency: string;
  aov_cents: number;
}

export interface ABAnalyticsResponse {
  node_id: string;
  from: string;
  to: string;
  variants: ABVariantRollup[];
}

// ─── Cost+ account link (per-project keys — STANDARDS.md § 1.1) ──────────────

/**
 * A Cost+ project linked to this merchant's account on the checkout service.
 * Every project carries its own API key (stored encrypted in Vault in
 * Connected mode; stored plaintext in localStorage in Studio mode — Studio
 * never actually makes a real Cost+ call, so the key is cosmetic).
 */
export interface CostplusProject {
  /** Cost+ gateway project id (UUID from the Cost+ dashboard). */
  id: string;
  /** Display name. */
  name: string;
  /** Display region / classification (e.g. "EU", "US"). */
  region: string;
  /** Cost+ gateway merchant id that owns this project. */
  costplus_merchant_id: string;
  /** True if this is the merchant's default-ish project (UI convenience). */
  is_default: boolean;
  /** ISO 8601 — when the project was added. */
  linked_at: string;
  /**
   * Optional per-project override of the Cost+ gateway host the
   * orchestrator calls when creating orders against this project. Empty
   * → use the orchestrator's default (current = api.costplus.online/v1;
   * legacy = api.nopayn.co.uk/v1).
   */
  api_base_url?: string;
}

export interface AddCostplusProjectInput {
  costplus_merchant_id: string;
  project_id: string;
  project_name: string;
  region?: string;
  api_key: string;
  is_default?: boolean;
  /** Optional per-project gateway host override. See CostplusProject.api_base_url. */
  api_base_url?: string;
}

// ─── Simulate ─────────────────────────────────────────────────────────────────

export interface SimulateInput {
  version_id?: string;
  context: Record<string, unknown>;
  source?: SourceBinding;
  trace_mode?: boolean;
}

// ─── Flow CRUD ────────────────────────────────────────────────────────────────

export interface CreateFlowInput {
  name: string;
  description?: string;
}

export interface UpdateFlowInput {
  name?: string;
  description?: string;
  is_default?: boolean;
}

export interface CreateFlowVersionInput {
  definition: unknown; // validated against FlowDefinition in impl
}

// ─── Standard API error envelope (API.md § Errors) ───────────────────────────

export interface ApiErrorBody {
  code: string;
  message: string;
  request_id?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.code = body.code;
    this.status = status;
    this.details = body.details;
  }
}
